import React, { ReactNode } from 'react';
import { Table } from '@tanstack/react-table';
import { Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensityLargeIcon from '@mui/icons-material/DensityLarge';
import GlobalFilter from './GlobalFilter';
import ColumnVisibilityMenu from './ColumnVisibilityMenu';
import { TanstackTableDensity } from './types';
import { useTanstackTableLocale } from './locale';

interface TanstackTableToolbarProps<TData> {
  table: Table<TData>;
  showGlobalFilter: boolean;
  showColumnVisibility: boolean;
  enableDensityToggle: boolean;
  density: TanstackTableDensity;
  onDensityChange: (density: TanstackTableDensity) => void;
  actions?: ReactNode;
}

function TanstackTableToolbar<TData>({
  table,
  showGlobalFilter,
  showColumnVisibility,
  enableDensityToggle,
  density,
  onDensityChange,
  actions,
}: TanstackTableToolbarProps<TData>): JSX.Element {
  const locale = useTanstackTableLocale();
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="space-between"
      sx={{ p: 1, flexWrap: 'wrap', gap: 1 }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {showGlobalFilter && (
          <GlobalFilter
            value={(table.getState().globalFilter as string) ?? ''}
            onChange={(value: string): void => table.setGlobalFilter(value)}
            label={locale.searchLabel}
            placeholder={locale.searchPlaceholder}
          />
        )}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
        {actions}
        {enableDensityToggle && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={density}
            onChange={(
              _: React.MouseEvent<HTMLElement>,
              value: TanstackTableDensity | null
            ): void => {
              if (value) onDensityChange(value);
            }}
          >
            <ToggleButton value="compact">
              <Tooltip title={locale.densityCompact}>
                <DensitySmallIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="standard">
              <Tooltip title={locale.densityStandard}>
                <DensityMediumIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="comfortable">
              <Tooltip title={locale.densityComfortable}>
                <DensityLargeIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        {showColumnVisibility && <ColumnVisibilityMenu table={table} />}
      </Stack>
    </Stack>
  );
}

export default TanstackTableToolbar;
